import { useEffect } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { Target } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useStats } from "@/hooks/useStats";
import { money } from "@/lib/money";
import { goalCalc } from "@/lib/goal";
import { t } from "@/lib/i18n";

function CountUp({ value, testId, className }) {
  const mv = useMotionValue(0);
  const txt = useTransform(mv, (v) => money(v));
  useEffect(() => { const c = animate(mv, value, { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }); return c.stop; }, [value, mv]);
  return <motion.span className={className} data-testid={testId}>{txt}</motion.span>;
}

function Ring({ pct }) {
  const r = 34, c = 2 * Math.PI * r, p = Math.min(100, Math.max(0, pct));
  return (
    <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90" data-testid="hero-spend-ring">
      <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,.2)" strokeWidth="8" />
      <motion.circle cx="40" cy="40" r={r} fill="none" stroke={pct > 100 ? "#fecaca" : "#fff"} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (p / 100) * c }} transition={{ duration: 1 }} />
    </svg>
  );
}

const Metric = ({ label, value, testId, neg }) => (
  <div className="mm-glass px-3 py-2.5">
    <div className="text-[11px] uppercase tracking-wider text-white/70">{label}</div>
    <div className={`num text-[15px] font-bold sm:text-base ${neg ? "text-red-200" : ""}`} data-testid={testId}>{money(value)}</div>
  </div>
);

export default function HeroCard({ onIncome, onSavings }) {
  const { db, mode } = useData();
  const st = useStats();
  const pct = st.inP > 0 ? (st.total / st.inP) * 100 : 0;
  const goal = goalCalc(db.settings.prefs, db.savings, st.s, st.e, mode);
  const past = new Date() >= st.e;
  return (
    <section className="mm-card mm-hero mm-rise bp-compact-hero" data-testid="hero-card">
      <div className="glow -right-10 -top-16 h-56 w-56 bg-white/40" />
      <div className="glow -bottom-20 left-10 h-48 w-48" style={{ background: "var(--ac)" }} />
      <div className="mm-grain absolute inset-0" /><div className="mm-hero-sheen" aria-hidden="true" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">{t(`Spent this ${mode}`)}</div>
            <CountUp value={st.total} testId="hero-total-spent-amount" className="num mt-2 block text-4xl font-bold sm:text-5xl lg:text-6xl" />
            <div className="mt-2 text-sm text-white/80" data-testid="hero-expense-count">{st.m.length} {t("expenses")}</div>
          </div>
          {st.inP > 0 && (
            <div className="relative shrink-0" title="Share of this period's income spent">
              <Ring pct={pct} />
              <div className="num absolute inset-0 grid place-items-center text-sm font-bold">{Math.round(pct)}%</div>
            </div>
          )}
        </div>

          <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3">
            <button onClick={onIncome} className="text-left"><Metric label={t("Income")} value={st.inP} testId="hero-income-amount" /></button>
            <Metric label={t(past ? (st.remaining < 0 ? "Overspent" : "Left over") : "Remaining")} value={st.remaining} testId="hero-remaining-amount" neg={st.remaining < 0} />
            <button onClick={onSavings} className="text-left"><Metric label={t("Savings")} value={past ? st.svA + Math.max(0, st.remaining) : st.svA} testId="hero-saved-amount" /></button>
          </div>

        {goal && (
          <button onClick={onSavings} className="mt-4 block w-full text-left" data-testid="hero-savings-goal-progress">
            <div className="flex items-center gap-2 text-sm font-medium"><Target className="h-4 w-4" /> {goal.text}</div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/20"><motion.i className="block h-full rounded-full bg-white" initial={{ width: 0 }} animate={{ width: `${goal.pct}%` }} transition={{ duration: 1 }} /></div>
          </button>
        )}

        <div className="mt-3 flex items-center justify-between border-t border-white/20 pt-2 text-sm text-white/80">
          <span>{t("Total spent in")} {st.year}</span><b className="num text-white" data-testid="hero-ytd-amount">{money(st.ytd)}</b>
        </div>
      </div>
    </section>
  );
}
